

import { motion, AnimatePresence } from "motion/react";
import { Building2, Zap, Target, Trophy, Cpu, Sparkles, CheckCircle2 } from "lucide-react";
import { useState, useEffect } from "react";

export const HeroVisual = () => {
  const [activeStage, setActiveStage] = useState(0);
  const stages = [
    { label: "01 Requirements", icon: <Building2 className="w-3 h-3" /> },
    { label: "02 Activation", icon: <Zap className="w-3 h-3" /> },
    { label: "03 Matching", icon: <Target className="w-3 h-3" /> },
    { label: "04 Outcome", icon: <Trophy className="w-3 h-3" /> }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveStage((prev) => (prev + 1) % 4);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="relative w-full h-full bg-[#050510] flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0 opacity-20" 
        style={{ backgroundImage: `radial-gradient(circle at 2px 2px, #00E396 1px, transparent 0)`, backgroundSize: '48px 48px' }} 
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#050510] via-transparent to-[#050510] z-0" />
      
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[1px] bg-gradient-to-r from-transparent via-brand-accent to-transparent opacity-50" />
      <div className="absolute top-1/4 -left-20 w-80 h-80 bg-brand-accent/20 rounded-full blur-[140px]" />
      <div className="absolute bottom-1/4 -right-20 w-96 h-96 bg-brand-accent/10 rounded-full blur-[160px]" />

      <div className="relative z-10 w-full max-w-5xl px-8 flex items-center justify-between gap-12 mt-[-40px]">
        <div className={`flex flex-col gap-6 w-48 transition-all duration-700 ${activeStage === 0 ? 'scale-105 opacity-100' : 'opacity-40 grayscale scale-95'}`}>
          <div className="flex items-center gap-3 px-3 py-1 bg-white/5 rounded-full border border-white/10 w-fit">
             <div className={`w-1.5 h-1.5 rounded-full ${activeStage === 0 ? 'bg-brand-accent animate-pulse' : 'bg-slate-600'}`} />
             <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Requirements</span>
          </div>
          <div className="p-4 rounded-2xl bg-white/5 border border-white/10 flex flex-col gap-3">
             <div className="h-2 w-full bg-white/10 rounded-full" />
             <div className="h-2 w-3/4 bg-white/5 rounded-full" />
             <div className="grid grid-cols-2 gap-2 mt-2">
                <div className="h-4 bg-brand-accent/10 border border-brand-accent/20 rounded-md" />
                <div className="h-4 bg-brand-accent/10 border border-brand-accent/20 rounded-md" />
             </div>
          </div>
          <div className="text-[9px] font-bold text-brand-accent uppercase tracking-widest text-center">Step 01: Share Goal</div>
        </div>

        <div className={`flex-1 flex flex-col items-center justify-center relative transition-all duration-700 ${activeStage === 1 || activeStage === 2 ? 'scale-110 opacity-100' : 'opacity-40 grayscale scale-90'}`}>
           <div className="relative w-64 h-64 flex items-center justify-center">
              <AnimatePresence>
                {(activeStage === 1 || activeStage === 2) && (
                   <motion.div
                     initial={{ opacity: 0 }}
                     animate={{ opacity: 1 }}
                     exit={{ opacity: 0 }}
                     className="absolute"
                   >
                     {[...Array(3)].map((_, i) => (
                        <motion.div
                          key={i}
                          animate={{ 
                            scale: [1, 1.8],
                            opacity: [0.6, 0]
                          }}
                          transition={{ duration: 2.5, delay: i * 0.8, repeat: Infinity, ease: "easeOut" }}
                          className="absolute inset-[-120px] border border-brand-accent/30 rounded-full"
                        />
                      ))}
                   </motion.div>
                )}
              </AnimatePresence>
              
              <motion.div 
                animate={{ rotate: 360 }}
                transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
                className={`w-40 h-40 rounded-[3rem] bg-brand-primary/40 backdrop-blur-3xl border flex items-center justify-center relative z-20 transition-colors duration-500 ${activeStage === 1 || activeStage === 2 ? 'border-brand-accent/60 shadow-[0_0_60px_rgba(0,227,150,0.3)]' : 'border-white/10 shadow-none'}`}
              >
                 <Cpu className={`w-16 h-16 transition-colors duration-500 ${activeStage === 1 || activeStage === 2 ? 'text-brand-accent animate-pulse' : 'text-slate-700'}`} />
                 <div className="absolute inset-2 border-2 border-dashed border-brand-accent/10 rounded-[2.5rem] animate-[spin_12s_linear_infinite]" />
              </motion.div>

              <AnimatePresence>
                {activeStage === 2 && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    {[...Array(10)].map((_, i) => (
                      <motion.div
                        key={`data-${i}`}
                        animate={{ 
                          x: [Math.cos(i * 36) * 220, 0],
                          y: [Math.sin(i * 36) * 220, 0],
                          opacity: [0, 1, 0]
                        }}
                        transition={{ duration: 1.5, delay: i * 0.15, repeat: Infinity, ease: "circIn" }}
                        className="absolute w-2 h-2 bg-brand-accent rounded-full blur-[2px]"
                      />
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
           </div>
           <div className={`mt-10 transition-opacity duration-500 ${activeStage === 1 || activeStage === 2 ? 'opacity-100' : 'opacity-20'}`}>
              <div className="text-[10px] font-black text-brand-accent uppercase tracking-[0.4em] mb-2">
                {activeStage === 1 ? "Activating Sourcing" : "Neural Matching Engine"}
              </div>
              <div className="flex gap-1 justify-center">
                 {[...Array(6)].map((_, i) => (
                    <motion.div 
                      key={i}
                      animate={{ height: activeStage === 1 || activeStage === 2 ? [4, 16, 4] : 4 }}
                      transition={{ duration: 0.4, delay: i * 0.1, repeat: Infinity }}
                      className="w-1 bg-brand-accent/50 rounded-full"
                    />
                 ))}
              </div>
           </div>
        </div>

        <div className={`w-64 space-y-4 transition-all duration-700 ${activeStage === 3 ? 'scale-105 opacity-100' : 'opacity-40 grayscale scale-95'}`}>
           <div className="flex items-center justify-between mb-4">
              <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest font-display">Step 03: Shortlist</span>
              {activeStage === 3 && <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-[10px] font-black text-brand-accent">HIRE CONFIRMED</motion.span>}
           </div>
           
           <motion.div 
             animate={activeStage === 3 ? { y: [0, -10, 0] } : {}}
             transition={{ duration: 2, repeat: Infinity }}
             className={`p-5 rounded-2xl bg-white/5 backdrop-blur-2xl border transition-all duration-500 relative overflow-hidden ${activeStage === 3 ? 'border-brand-accent/40 shadow-[0_20px_50px_rgba(0,227,150,0.2)] bg-white/10' : 'border-white/5'}`}
           >
             <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-full bg-slate-800 border border-white/10 flex items-center justify-center relative overflow-hidden">
                   <img 
                     src="https://picsum.photos/seed/candidate42/100/100" 
                     alt="Candidate" 
                     className={`w-full h-full object-cover transition-all ${activeStage === 3 ? 'scale-110 grayscale-0' : 'opacity-40 grayscale'}`}
                     referrerPolicy="no-referrer"
                   />
                </div>
                <div className="flex-1">
                   <div className={`h-2 w-24 rounded-full mb-2 ${activeStage === 3 ? 'bg-brand-accent/40' : 'bg-white/10'}`} />
                   <div className="h-1.5 w-16 bg-white/5 rounded-full" />
                </div>
                <div className="text-right">
                   <div className={`text-[12px] font-black italic ${activeStage === 3 ? 'text-brand-accent' : 'text-slate-600'}`}>98% Fit</div>
                </div>
             </div>
             <div className="flex gap-2">
                <div className="px-3 py-1 bg-brand-accent/10 border border-brand-accent/20 rounded-md text-[9px] font-black text-brand-accent uppercase">Outcome Locked</div>
             </div>
             {activeStage === 3 && (
                <motion.div 
                  initial={{ top: '0%' }}
                  animate={{ top: '100%' }}
                  transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
                  className="absolute left-0 w-full h-[2px] bg-brand-accent shadow-[0_0_15px_rgba(0,227,150,1)] z-10"
                />
             )}
           </motion.div>
           <div className="text-[9px] font-bold text-gray-500 uppercase tracking-widest text-center mt-4">Step 04: Zero Upfront Risk</div>
        </div>
      </div>

      <div className="absolute inset-0 pointer-events-none z-30">
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 w-full max-w-2xl">
           <div className="flex items-center justify-between px-8 bg-black/40 backdrop-blur-xl border border-white/10 py-4 rounded-full">
              {stages.map((stage, idx) => (
                <div key={idx} className="flex items-center gap-3 relative">
                   <motion.div 
                     animate={{ 
                       background: activeStage === idx ? 'rgba(0,227,150,1)' : 'rgba(255,255,255,0.05)',
                       scale: activeStage === idx ? 1.2 : 1
                     }}
                     className="w-7 h-7 rounded-full flex items-center justify-center text-white border border-white/10 shadow-lg"
                   >
                     {stage.icon}
                   </motion.div>
                   <span className={`text-[10px] font-black uppercase tracking-widest transition-all duration-500 ${activeStage === idx ? 'text-white' : 'text-white/20'}`}>
                     {stage.label}
                   </span>
                   {idx < 3 && (
                     <div className="absolute left-[calc(100%+12px)] w-8 h-[1px] bg-white/10" />
                   )}
                </div>
              ))}
           </div>
        </div>

        <div className="absolute top-10 left-10 right-10 flex items-center gap-6">
           <div className="text-[10px] font-black text-brand-accent font-mono tracking-widest bg-brand-accent/10 px-3 py-1 rounded-md border border-brand-accent/20">LIVE_HIRING_WORKFLOW</div>
           <div className="flex-1 h-[2px] bg-white/5 rounded-full relative overflow-hidden">
              <motion.div 
                animate={{ width: `${((activeStage + 1) / 4) * 100}%` }}
                className="h-full bg-brand-accent"
              />
           </div>
           <div className="flex gap-4 items-center">
              <div className="w-1.5 h-1.5 rounded-full bg-brand-accent animate-pulse" />
              <span className="text-[10px] font-black text-white/50 uppercase tracking-widest">Processing...</span>
           </div>
        </div>
      </div>
    </div>
  );
};

export const Hero = ({ onHiringStart }: { onHiringStart: () => void }) => {
  return (
    <section className="relative pt-32 pb-24 overflow-hidden bg-brand-secondary/30">
      <div className="blob w-[500px] h-[500px] bg-brand-accent top-[-10%] left-[-10%]" />
      <div className="blob w-[400px] h-[400px] bg-brand-deep bottom-[-5%] right-[-5%] delay-700" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          >
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-brand-accent/10 text-brand-deep text-xs font-black uppercase tracking-[0.2em] mb-8 border border-brand-accent/20 shadow-sm backdrop-blur-md">
              Focused Full-time Hiring Platform
            </span>
            <h1 className="text-6xl md:text-8xl font-display font-bold tracking-tight text-brand-primary leading-[1.1] mb-8 text-balance">
              Hire Faster. <br />
              <span className="text-brand-accent italic underline decoration-brand-accent/30 underline-offset-[12px]">Pay Only When You Hire.</span>
            </h1>
            <p className="text-xl md:text-2xl text-brand-muted mb-12 max-w-3xl mx-auto leading-relaxed font-medium">
              QuantumHire combines the scale of a platform with the execution of a recruitment partner — so you don’t just get candidates, you get hires.
            </p>
            
            <div className="flex justify-center mb-8">
              <motion.button 
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={onHiringStart}
                className="px-12 py-6 bg-brand-primary text-white rounded-3xl font-black text-lg uppercase tracking-[0.2em] hover:bg-brand-deep transition-all shadow-[0_20px_40px_-10px_rgba(0,0,0,0.3)] flex items-center gap-4 group"
              >
                Start Hiring Now
                <Zap className="w-5 h-5 text-brand-accent fill-brand-accent group-hover:animate-pulse" />
              </motion.button>
            </div>

            <div className="flex flex-wrap justify-center items-center gap-x-6 gap-y-2 text-[10px] md:text-xs font-black text-brand-primary/60 uppercase tracking-widest mb-16">
               <span className="flex items-center gap-2">3x faster hiring</span>
               <span className="text-brand-accent text-lg leading-none">•</span>
               <span className="flex items-center gap-2">10x recruiter productivity</span>
               <span className="text-brand-accent text-lg leading-none">•</span>
               <span className="flex items-center gap-2">Pay only when you hire</span>
            </div>

            <div className="flex items-center justify-center gap-4 mb-16 px-6 py-3 rounded-2xl bg-white/40 backdrop-blur-sm border border-white/60 mx-auto w-fit shadow-sm">
               <Sparkles className="w-4 h-4 text-brand-accent" />
               <span className="text-sm md:text-base font-bold text-brand-primary opacity-80 italic">
                 Built for employers who want to hire faster — <span className="text-brand-accent not-italic font-black underline decoration-brand-accent/20">without upfront costs</span>
               </span>
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 60 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2, duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
          className="relative max-w-6xl mx-auto"
        >
          <div className="relative rounded-[4rem] overflow-hidden shadow-[0_64px_128px_-32px_rgba(0,0,0,0.15)] border-[8px] border-white bg-white">
            <div className="aspect-[16/10] flex items-center justify-center relative">
               <HeroVisual />
            </div>
          </div>
          <motion.div 
            animate={{ y: [0, -20, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            className="absolute -top-10 -left-10 p-6 glass rounded-3xl border-white shadow-xl z-20 hidden lg:block"
          >
             <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-brand-accent/20 flex items-center justify-center text-brand-accent"><CheckCircle2 className="w-5 h-5"/></div>
                <div>
                   <p className="text-xs font-black text-brand-primary">Candidate Matched</p>
                   <p className="text-[10px] text-gray-400">98% Fit Score</p>
                </div>
             </div>
          </motion.div>
        </motion.div>
      </div>

      <div className="absolute top-0 left-0 w-full h-[800px] bg-gradient-to-b from-brand-accent/5 to-transparent -z-10" />
    </section>
  );
};
